<template>
  <div class="space-y-6">
    <section
      class="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Feature access explorer
          </h2>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Search a store or group, inspect the effective subscription access,
            and apply a manual store override when the feature needs to be
            enabled outside the assigned plan.
          </p>
        </div>

        <div
          class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40 xl:max-w-lg"
        >
          <p
            class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
          >
            Feature catalog
          </p>
          <div v-if="featureCatalog.length" class="mt-3 flex flex-wrap gap-2">
            <button
              v-for="feature in visibleFeatureCatalog"
              :key="feature"
              type="button"
              class="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:border-blue-400 hover:text-blue-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-blue-500 dark:hover:text-blue-300"
              @click="applyFeatureSelection(feature)"
            >
              {{ feature }}
            </button>
          </div>
          <p v-else class="mt-3 text-xs text-gray-500 dark:text-gray-400">
            No plan features have been defined yet.
          </p>
          <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Keys are discovered from plan feature JSON so you can pick rather
            than retype them.
          </p>
        </div>
      </div>

      <datalist id="subscription-feature-catalog">
        <option v-for="feature in featureCatalog" :key="feature" :value="feature" />
      </datalist>
    </section>

    <div class="grid gap-6 xl:grid-cols-2">
      <section
        class="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Store feature access
            </h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Search for any store, inspect its effective subscription state,
              and save a manual override when a feature should be granted.
            </p>
          </div>
          <button
            type="button"
            class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            :disabled="storeSearchLoading"
            @click="fetchStoreCandidates"
          >
            <i
              :class="[
                'fas mr-2',
                storeSearchLoading ? 'fa-sync fa-spin' : 'fa-sync',
              ]"
            ></i>
            Refresh list
          </button>
        </div>

        <div class="mt-4 space-y-4">
          <div>
            <label
              class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Store search
            </label>
            <div class="relative mt-1">
              <input
                v-model="storeSearch"
                type="search"
                placeholder="Search by store name, ID, or merchant code…"
                class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                @input="queueStoreSearch"
              />
              <span
                class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400"
              >
                <i
                  :class="[
                    'fas',
                    storeSearchLoading ? 'fa-sync fa-spin' : 'fa-search',
                  ]"
                ></i>
              </span>
            </div>
            <p
              v-if="storeSearchError"
              class="mt-2 text-xs text-red-600 dark:text-red-300"
            >
              {{ storeSearchError }}
            </p>
          </div>

          <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <div class="max-h-60 overflow-y-auto">
              <button
                v-for="item in storeResults"
                :key="item.id"
                type="button"
                class="block w-full border-b border-gray-200 px-4 py-3 text-left text-sm transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:hover:bg-blue-900/30"
                :class="{
                  'bg-blue-50 dark:bg-blue-900/30': selectedStore?.id === item.id,
                }"
                @click="selectStore(item)"
              >
                <p class="font-semibold text-gray-900 dark:text-gray-100">
                  {{ item.name || "Unnamed store" }}
                  <span class="text-xs text-gray-500">#{{ item.id }}</span>
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  Merchant: {{ item.merchant_code || "—" }}
                  <span v-if="item.location">• {{ item.location }}</span>
                </p>
              </button>
              <div
                v-if="!storeResults.length && !storeSearchLoading"
                class="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                No stores matched this search.
              </div>
            </div>
            <pagination-controls
              v-if="storePagination.total > 0"
              :current-page="storePagination.page"
              :total-pages="storePagination.pages"
              :total="storePagination.total"
              :per-page="storePagination.perPage"
              :has-next="storePagination.hasNext"
              :has-prev="storePagination.hasPrev"
              items-label="stores"
              :show-per-page-selector="false"
              :show-first-last="false"
              :max-visible-pages="3"
              @page-change="handleStorePageChange"
            />
          </div>

          <div
            v-if="selectedStore"
            class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">
                  {{ selectedStore.name || "Unnamed store" }}
                  <span class="text-xs text-gray-500">#{{ selectedStore.id }}</span>
                </p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Merchant: {{ selectedStore.merchant_code || "—" }}
                  <span v-if="selectedStore.location">
                    • {{ selectedStore.location }}
                  </span>
                </p>
              </div>
              <button
                type="button"
                class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
                :disabled="storeStatusLoading"
                @click="loadStoreStatus"
              >
                <i
                  :class="[
                    'fas mr-2',
                    storeStatusLoading ? 'fa-sync fa-spin' : 'fa-eye',
                  ]"
                ></i>
                Refresh status
              </button>
            </div>

            <div v-if="storeStatusLoading" class="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Loading store status…
            </div>
            <div
              v-else-if="storeStatusError"
              class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200"
            >
              {{ storeStatusError }}
            </div>
            <div v-else-if="storeStatus" class="mt-4 space-y-4">
              <div class="flex flex-wrap gap-2 text-xs">
                <span
                  class="rounded-full px-3 py-1 font-semibold"
                  :class="statusBadgeClass(storeStatus.status)"
                >
                  {{ storeStatus.status }}
                </span>
                <span
                  class="rounded-full bg-gray-200 px-3 py-1 font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                >
                  {{ sourceLabel(storeStatus.subscription_source || 'NONE') }}
                </span>
                <span
                  v-if="storeStatus.in_automatic_grace"
                  class="rounded-full bg-amber-100 px-3 py-1 font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-200"
                >
                  Automatic grace
                </span>
              </div>

              <div class="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                <p v-if="storeStatus.plan">
                  Plan:
                  <strong>{{ storeStatus.plan.display_name }}</strong>
                </p>
                <p v-if="storeStatus.expires_at">
                  Expires {{ formatDate(storeStatus.expires_at) }}
                </p>
                <p v-if="storeStatus.grace_reason">
                  {{ storeStatus.grace_reason }}
                </p>
              </div>

              <div v-if="storeOverrideEntries.length" class="space-y-2">
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Manual overrides
                </p>
                <div class="flex flex-wrap gap-2 text-xs">
                  <span
                    v-for="([key, value]) in storeOverrideEntries"
                    :key="key"
                    class="rounded-full bg-emerald-100 px-2 py-1 font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
                  >
                    {{ key }}: {{ renderFeatureValue(value) }}
                  </span>
                </div>
              </div>

              <div v-if="storeEffectiveFeatureEntries.length" class="space-y-2">
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Effective feature set
                </p>
                <div class="flex flex-wrap gap-2 text-xs">
                  <span
                    v-for="([key, value]) in storeEffectiveFeatureEntries"
                    :key="key"
                    class="rounded-full bg-blue-100 px-2 py-1 font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                  >
                    {{ key }}: {{ renderFeatureValue(value) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="grid gap-4">
            <div>
              <label
                class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                Feature key
              </label>
              <input
                v-model="storeFeatureKey"
                type="text"
                list="subscription-feature-catalog"
                placeholder="analytics"
                class="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label
                class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                Override value
              </label>
              <input
                v-model="storeFeatureValueInput"
                type="text"
                placeholder="true"
                class="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                JSON supported: <strong>true</strong>, <strong>5000</strong>,
                <strong>"beta"</strong>, or an object.
              </p>
              <p
                v-if="hasSelectedStoreOverride"
                class="mt-1 text-xs text-emerald-600 dark:text-emerald-300"
              >
                Current override: {{ renderFeatureValue(selectedStoreOverrideValue) }}
              </p>
            </div>

            <div class="flex flex-wrap gap-3">
              <button
                type="button"
                class="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                :disabled="storeFeatureLoading"
                @click="checkStoreFeature"
              >
                <i
                  :class="[
                    'fas mr-2',
                    storeFeatureLoading ? 'fa-sync fa-spin' : 'fa-search',
                  ]"
                ></i>
                Check access
              </button>
              <button
                type="button"
                class="inline-flex items-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
                :disabled="storeFeatureSaving"
                @click="saveStoreOverride"
              >
                <i
                  :class="[
                    'fas mr-2',
                    storeFeatureSaving ? 'fa-sync fa-spin' : 'fa-toggle-on',
                  ]"
                ></i>
                {{ hasSelectedStoreOverride ? 'Update override' : 'Save store override' }}
              </button>
              <button
                type="button"
                class="inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:bg-gray-900 dark:text-red-300 dark:hover:bg-red-900/20"
                :disabled="!hasSelectedStoreOverride || storeFeatureRemoving"
                @click="removeStoreOverride"
              >
                <i
                  :class="[
                    'fas mr-2',
                    storeFeatureRemoving ? 'fa-sync fa-spin' : 'fa-trash',
                  ]"
                ></i>
                Remove override
              </button>
            </div>

            <div
              v-if="storeFeatureError"
              class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200"
            >
              {{ storeFeatureError }}
            </div>

            <div
              v-if="storeFeatureResult"
              class="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-900/40"
            >
              <div class="flex items-center justify-between gap-3">
                <p class="font-semibold text-gray-900 dark:text-white">
                  Result for {{ storeFeatureResult.feature_name }}
                </p>
                <span
                  class="rounded-full px-3 py-1 text-xs font-semibold"
                  :class="accessBadgeClass(storeFeatureResult.has_access)"
                >
                  {{ storeFeatureResult.has_access ? 'Available' : 'Not available' }}
                </span>
              </div>
              <div class="mt-3 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                <p>
                  Source:
                  <strong>{{ sourceLabel(storeFeatureResult.source) }}</strong>
                </p>
                <p>
                  Value:
                  <strong>{{ renderFeatureValue(storeFeatureResult.feature_value) }}</strong>
                </p>
                <p v-if="storeFeatureResult.grace_reason">
                  {{ storeFeatureResult.grace_reason }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        class="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Group feature access
            </h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Search a group and inspect feature access from the active group
              subscription.
            </p>
          </div>
          <button
            type="button"
            class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            :disabled="groupSearchLoading"
            @click="fetchGroupCandidates"
          >
            <i
              :class="[
                'fas mr-2',
                groupSearchLoading ? 'fa-sync fa-spin' : 'fa-sync',
              ]"
            ></i>
            Refresh list
          </button>
        </div>

        <div class="mt-4 space-y-4">
          <div>
            <label
              class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
            >
              Group search
            </label>
            <div class="relative mt-1">
              <input
                v-model="groupSearch"
                type="search"
                placeholder="Search by group name or ID…"
                class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                @input="queueGroupSearch"
              />
              <span
                class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400"
              >
                <i
                  :class="[
                    'fas',
                    groupSearchLoading ? 'fa-sync fa-spin' : 'fa-search',
                  ]"
                ></i>
              </span>
            </div>
            <p
              v-if="groupSearchError"
              class="mt-2 text-xs text-red-600 dark:text-red-300"
            >
              {{ groupSearchError }}
            </p>
          </div>

          <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <div class="max-h-60 overflow-y-auto">
              <button
                v-for="item in groupResults"
                :key="item.id"
                type="button"
                class="block w-full border-b border-gray-200 px-4 py-3 text-left text-sm transition hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-700 dark:hover:bg-purple-900/30"
                :class="{
                  'bg-purple-50 dark:bg-purple-900/30': selectedGroup?.id === item.id,
                }"
                @click="selectGroup(item)"
              >
                <p class="font-semibold text-gray-900 dark:text-gray-100">
                  {{ item.name || "Unnamed group" }}
                  <span class="text-xs text-gray-500">#{{ item.id }}</span>
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{ item.description || 'No description' }}
                </p>
              </button>
              <div
                v-if="!groupResults.length && !groupSearchLoading"
                class="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
              >
                No groups matched this search.
              </div>
            </div>
            <pagination-controls
              v-if="groupPagination.total > 0"
              :current-page="groupPagination.page"
              :total-pages="groupPagination.pages"
              :total="groupPagination.total"
              :per-page="groupPagination.perPage"
              :has-next="groupPagination.hasNext"
              :has-prev="groupPagination.hasPrev"
              items-label="groups"
              :show-per-page-selector="false"
              :show-first-last="false"
              :max-visible-pages="3"
              @page-change="handleGroupPageChange"
            />
          </div>

          <div
            v-if="selectedGroup"
            class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">
                  {{ selectedGroup.name || 'Unnamed group' }}
                  <span class="text-xs text-gray-500">#{{ selectedGroup.id }}</span>
                </p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {{ selectedGroup.description || 'No description' }}
                </p>
              </div>
              <button
                type="button"
                class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
                :disabled="groupStatusLoading"
                @click="loadGroupStatus"
              >
                <i
                  :class="[
                    'fas mr-2',
                    groupStatusLoading ? 'fa-sync fa-spin' : 'fa-eye',
                  ]"
                ></i>
                Refresh status
              </button>
            </div>

            <div v-if="groupStatusLoading" class="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Loading group status…
            </div>
            <div
              v-else-if="groupStatusError"
              class="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200"
            >
              {{ groupStatusError }}
            </div>
            <div v-else-if="groupStatus" class="mt-4 space-y-4">
              <div class="flex flex-wrap gap-2 text-xs">
                <span
                  class="rounded-full px-3 py-1 font-semibold"
                  :class="statusBadgeClass(groupStatus.status)"
                >
                  {{ groupStatus.status }}
                </span>
                <span
                  class="rounded-full bg-gray-200 px-3 py-1 font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                >
                  {{ sourceLabel(groupStatus.subscription_source || 'NONE') }}
                </span>
              </div>

              <div class="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                <p v-if="groupStatus.plan">
                  Plan:
                  <strong>{{ groupStatus.plan.display_name }}</strong>
                </p>
                <p v-if="groupStatus.expires_at">
                  Expires {{ formatDate(groupStatus.expires_at) }}
                </p>
              </div>

              <div v-if="groupFeatureEntries.length" class="space-y-2">
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Effective feature set
                </p>
                <div class="flex flex-wrap gap-2 text-xs">
                  <span
                    v-for="([key, value]) in groupFeatureEntries"
                    :key="key"
                    class="rounded-full bg-purple-100 px-2 py-1 font-medium text-purple-800 dark:bg-purple-900/40 dark:text-purple-200"
                  >
                    {{ key }}: {{ renderFeatureValue(value) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="grid gap-4">
            <div>
              <label
                class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                Feature key
              </label>
              <input
                v-model="groupFeatureKey"
                type="text"
                list="subscription-feature-catalog"
                placeholder="inventory_transfer"
                class="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              />
            </div>

            <div class="flex flex-wrap gap-3">
              <button
                type="button"
                class="inline-flex items-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-60"
                :disabled="groupFeatureLoading"
                @click="checkGroupFeature"
              >
                <i
                  :class="[
                    'fas mr-2',
                    groupFeatureLoading ? 'fa-sync fa-spin' : 'fa-search',
                  ]"
                ></i>
                Check access
              </button>
            </div>

            <div
              v-if="groupFeatureError"
              class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200"
            >
              {{ groupFeatureError }}
            </div>

            <div
              v-if="groupFeatureResult"
              class="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-900/40"
            >
              <div class="flex items-center justify-between gap-3">
                <p class="font-semibold text-gray-900 dark:text-white">
                  Result for {{ groupFeatureResult.feature_name }}
                </p>
                <span
                  class="rounded-full px-3 py-1 text-xs font-semibold"
                  :class="accessBadgeClass(groupFeatureResult.has_access)"
                >
                  {{ groupFeatureResult.has_access ? 'Available' : 'Not available' }}
                </span>
              </div>
              <div class="mt-3 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                <p>
                  Source:
                  <strong>{{ sourceLabel(groupFeatureResult.source) }}</strong>
                </p>
                <p>
                  Value:
                  <strong>{{ renderFeatureValue(groupFeatureResult.feature_value) }}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import PaginationControls from "@/components/common/PaginationControls.vue";

export default {
  name: "SubscriptionFeatureChecksPanel",
  components: {
    PaginationControls,
  },
  props: {
    plans: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const defaultSuggestionPerPage = 8;
    const createSuggestionPagination = () => ({
      page: 1,
      perPage: defaultSuggestionPerPage,
      total: 0,
      pages: 0,
      hasNext: false,
      hasPrev: false,
    });

    const storeSearch = ref("");
    const storeResults = ref([]);
    const storePagination = ref(createSuggestionPagination());
    const storeSearchLoading = ref(false);
    const storeSearchError = ref("");
    const selectedStore = ref(null);
    const storeStatus = ref(null);
    const storeStatusLoading = ref(false);
    const storeStatusError = ref("");
    const storeFeatureKey = ref("");
    const storeFeatureValueInput = ref("true");
    const storeFeatureLoading = ref(false);
    const storeFeatureSaving = ref(false);
    const storeFeatureRemoving = ref(false);
    const storeFeatureError = ref("");
    const storeFeatureResult = ref(null);

    const groupSearch = ref("");
    const groupResults = ref([]);
  const groupPagination = ref(createSuggestionPagination());
    const groupSearchLoading = ref(false);
    const groupSearchError = ref("");
    const selectedGroup = ref(null);
    const groupStatus = ref(null);
    const groupStatusLoading = ref(false);
    const groupStatusError = ref("");
    const groupFeatureKey = ref("");
    const groupFeatureLoading = ref(false);
    const groupFeatureError = ref("");
    const groupFeatureResult = ref(null);

    let storeSearchTimer = null;
    let groupSearchTimer = null;

    const featureCatalog = computed(() => {
      const keys = new Set();

      (props.plans || []).forEach((plan) => {
        Object.keys(plan?.features || {}).forEach((key) => {
          if (key) {
            keys.add(key);
          }
        });
      });

      return Array.from(keys).sort((left, right) =>
        left.localeCompare(right)
      );
    });

    const visibleFeatureCatalog = computed(() =>
      featureCatalog.value.slice(0, 24)
    );

    const normalizedStoreFeatureKey = computed(() => storeFeatureKey.value.trim());
    const normalizedGroupFeatureKey = computed(() => groupFeatureKey.value.trim());

    const storeOverrideEntries = computed(() =>
      Object.entries(storeStatus.value?.feature_overrides || {})
    );
    const storeEffectiveFeatureEntries = computed(() =>
      Object.entries(storeStatus.value?.effective_features || {})
    );
    const groupFeatureEntries = computed(() =>
      Object.entries(groupStatus.value?.features || {})
    );

    const hasStoreOverrideValue = (featureName) => {
      if (!featureName) {
        return false;
      }

      return Object.prototype.hasOwnProperty.call(
        storeStatus.value?.feature_overrides || {},
        featureName
      );
    };

    const hasSelectedStoreOverride = computed(() =>
      hasStoreOverrideValue(normalizedStoreFeatureKey.value)
    );

    const selectedStoreOverrideValue = computed(() => {
      if (!hasSelectedStoreOverride.value) {
        return null;
      }

      return storeStatus.value.feature_overrides[normalizedStoreFeatureKey.value];
    });

    const renderFeatureValue = (value) => {
      if (typeof value === "boolean") {
        return value ? "Enabled" : "Disabled";
      }
      if (value === null || value === undefined) {
        return "—";
      }
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return value;
    };

    const formatFeatureInputValue = (value) => {
      if (value === undefined) {
        return "true";
      }
      return JSON.stringify(value);
    };

    const formatDate = (value) => {
      if (!value) {
        return "—";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const statusBadgeClass = (status) => {
      const classes = {
        ACTIVE:
          "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200",
        AUTOMATIC_GRACE:
          "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200",
        GRACE_PERIOD:
          "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-200",
        NO_SUBSCRIPTION:
          "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-200",
      };

      return (
        classes[status] ||
        "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
      );
    };

    const accessBadgeClass = (hasAccess) =>
      hasAccess
        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200"
        : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-200";

    const sourceLabel = (source) => {
      const labels = {
        AUTOMATIC_GRACE: "Automatic grace",
        STORE_OVERRIDE: "Manual store override",
        STORE: "Store plan",
        GROUP: "Group plan",
        NONE: "No entitlement",
      };

      return labels[source] || source || "Unknown";
    };

    const applyFeatureSelection = (feature) => {
      storeFeatureKey.value = feature;
      groupFeatureKey.value = feature;
    };

    const fetchStoreCandidates = async () => {
      storeSearchLoading.value = true;
      storeSearchError.value = "";

      try {
        const response = await http.get("/v1/admin/stores/lookup", {
          params: {
            search: storeSearch.value.trim(),
            page: storePagination.value.page,
            per_page: storePagination.value.perPage,
          },
        });
        const data = response?.data || {};
        storeResults.value = data?.items || [];
        storePagination.value = {
          page: data?.page || 1,
          perPage: data?.per_page || storePagination.value.perPage,
          total: data?.total || 0,
          pages: data?.pages || 0,
          hasNext: data?.has_next || false,
          hasPrev: data?.has_prev || false,
        };
      } catch (error) {
        console.error("Failed to load store candidates", error);
        storeSearchError.value =
          error?.response?.data?.error?.message ||
          "Unable to load store results.";
      } finally {
        storeSearchLoading.value = false;
      }
    };

    const fetchGroupCandidates = async () => {
      groupSearchLoading.value = true;
      groupSearchError.value = "";

      try {
        const response = await http.get("/v1/admin/groups/lookup", {
          params: {
            search: groupSearch.value.trim(),
            page: groupPagination.value.page,
            per_page: groupPagination.value.perPage,
          },
        });
        const data = response?.data || {};
        groupResults.value = data?.items || [];
        groupPagination.value = {
          page: data?.page || 1,
          perPage: data?.per_page || groupPagination.value.perPage,
          total: data?.total || 0,
          pages: data?.pages || 0,
          hasNext: data?.has_next || false,
          hasPrev: data?.has_prev || false,
        };
      } catch (error) {
        console.error("Failed to load group candidates", error);
        groupSearchError.value =
          error?.response?.data?.error?.message ||
          "Unable to load group results.";
      } finally {
        groupSearchLoading.value = false;
      }
    };

    const queueStoreSearch = () => {
      clearTimeout(storeSearchTimer);
      storePagination.value.page = 1;
      storeSearchTimer = setTimeout(() => {
        fetchStoreCandidates();
      }, 250);
    };

    const queueGroupSearch = () => {
      clearTimeout(groupSearchTimer);
      groupPagination.value.page = 1;
      groupSearchTimer = setTimeout(() => {
        fetchGroupCandidates();
      }, 250);
    };

    const handleStorePageChange = (page) => {
      if (!page || page === storePagination.value.page) {
        return;
      }

      storePagination.value.page = page;
      fetchStoreCandidates();
    };

    const handleGroupPageChange = (page) => {
      if (!page || page === groupPagination.value.page) {
        return;
      }

      groupPagination.value.page = page;
      fetchGroupCandidates();
    };

    const selectStore = (item) => {
      selectedStore.value = item;
    };

    const selectGroup = (item) => {
      selectedGroup.value = item;
    };

    const loadStoreStatus = async () => {
      if (!selectedStore.value?.id) {
        return;
      }

      storeStatusLoading.value = true;
      storeStatusError.value = "";

      try {
        const response = await http.get(
          `/v1/admin/stores/${selectedStore.value.id}/subscription`
        );
        storeStatus.value = response?.data || null;
      } catch (error) {
        console.error("Failed to load store status", error);
        storeStatusError.value =
          error?.response?.data?.error?.message ||
          "Unable to load store subscription status.";
      } finally {
        storeStatusLoading.value = false;
      }
    };

    const loadGroupStatus = async () => {
      if (!selectedGroup.value?.id) {
        return;
      }

      groupStatusLoading.value = true;
      groupStatusError.value = "";

      try {
        const response = await http.get(
          `/v1/admin/groups/${selectedGroup.value.id}/subscription`
        );
        groupStatus.value = response?.data || null;
      } catch (error) {
        console.error("Failed to load group status", error);
        groupStatusError.value =
          error?.response?.data?.error?.message ||
          "Unable to load group subscription status.";
      } finally {
        groupStatusLoading.value = false;
      }
    };

    const checkStoreFeature = async () => {
      if (!selectedStore.value?.id || !normalizedStoreFeatureKey.value) {
        storeFeatureError.value = "Select a store and feature key first.";
        return;
      }

      storeFeatureLoading.value = true;
      storeFeatureError.value = "";
      storeFeatureResult.value = null;

      try {
        const response = await http.get(
          `/v1/admin/stores/${selectedStore.value.id}/subscription/feature/${encodeURIComponent(
            normalizedStoreFeatureKey.value
          )}`
        );
        storeFeatureResult.value = response?.data || null;
      } catch (error) {
        console.error("Failed to check store feature", error);
        storeFeatureError.value =
          error?.response?.data?.error?.message ||
          "Unable to check store feature access.";
      } finally {
        storeFeatureLoading.value = false;
      }
    };

    const checkGroupFeature = async () => {
      if (!selectedGroup.value?.id || !normalizedGroupFeatureKey.value) {
        groupFeatureError.value = "Select a group and feature key first.";
        return;
      }

      groupFeatureLoading.value = true;
      groupFeatureError.value = "";
      groupFeatureResult.value = null;

      try {
        const response = await http.get(
          `/v1/admin/groups/${selectedGroup.value.id}/subscription/feature/${encodeURIComponent(
            normalizedGroupFeatureKey.value
          )}`
        );
        groupFeatureResult.value = response?.data || null;
      } catch (error) {
        console.error("Failed to check group feature", error);
        groupFeatureError.value =
          error?.response?.data?.error?.message ||
          "Unable to check group feature access.";
      } finally {
        groupFeatureLoading.value = false;
      }
    };

    const parseStoreOverrideValue = () => {
      const rawValue = storeFeatureValueInput.value.trim();

      if (!rawValue) {
        return true;
      }

      try {
        return JSON.parse(rawValue);
      } catch (error) {
        throw new Error(
          "Override value must be valid JSON. Use true, 5000, \"beta\", or an object."
        );
      }
    };

    const saveStoreOverride = async () => {
      if (!selectedStore.value?.id || !normalizedStoreFeatureKey.value) {
        storeFeatureError.value = "Select a store and feature key first.";
        return;
      }

      let parsedValue;
      try {
        parsedValue = parseStoreOverrideValue();
      } catch (error) {
        storeFeatureError.value = error.message;
        return;
      }

      storeFeatureSaving.value = true;
      storeFeatureError.value = "";

      try {
        await http.put(
          `/v1/admin/stores/${selectedStore.value.id}/subscription/feature-overrides/${encodeURIComponent(
            normalizedStoreFeatureKey.value
          )}`,
          {
            feature_value: parsedValue,
          }
        );

        toast.success(`Saved override for ${normalizedStoreFeatureKey.value}.`);
        await loadStoreStatus();
        await checkStoreFeature();
      } catch (error) {
        console.error("Failed to save store override", error);
        storeFeatureError.value =
          error?.response?.data?.error?.message ||
          "Unable to save the store override.";
      } finally {
        storeFeatureSaving.value = false;
      }
    };

    const removeStoreOverride = async () => {
      if (!selectedStore.value?.id || !normalizedStoreFeatureKey.value) {
        storeFeatureError.value = "Select a store and feature key first.";
        return;
      }

      storeFeatureRemoving.value = true;
      storeFeatureError.value = "";

      try {
        await http.delete(
          `/v1/admin/stores/${selectedStore.value.id}/subscription/feature-overrides/${encodeURIComponent(
            normalizedStoreFeatureKey.value
          )}`
        );

        toast.success(`Removed override for ${normalizedStoreFeatureKey.value}.`);
        storeFeatureValueInput.value = "true";
        await loadStoreStatus();
        await checkStoreFeature();
      } catch (error) {
        console.error("Failed to remove store override", error);
        storeFeatureError.value =
          error?.response?.data?.error?.message ||
          "Unable to remove the store override.";
      } finally {
        storeFeatureRemoving.value = false;
      }
    };

    watch(selectedStore, async (item) => {
      storeStatus.value = null;
      storeStatusError.value = "";
      storeFeatureError.value = "";
      storeFeatureResult.value = null;

      if (item?.id) {
        await loadStoreStatus();
      }
    });

    watch(selectedGroup, async (item) => {
      groupStatus.value = null;
      groupStatusError.value = "";
      groupFeatureError.value = "";
      groupFeatureResult.value = null;

      if (item?.id) {
        await loadGroupStatus();
      }
    });

    watch(
      [normalizedStoreFeatureKey, storeStatus],
      ([featureName]) => {
        if (!featureName) {
          storeFeatureValueInput.value = "true";
          return;
        }

        if (hasStoreOverrideValue(featureName)) {
          storeFeatureValueInput.value = formatFeatureInputValue(
            storeStatus.value.feature_overrides[featureName]
          );
        } else {
          storeFeatureValueInput.value = "true";
        }
      },
      { deep: true }
    );

    onMounted(() => {
      fetchStoreCandidates();
      fetchGroupCandidates();
    });

    return {
      storeSearch,
      storeResults,
      storePagination,
      storeSearchLoading,
      storeSearchError,
      selectedStore,
      storeStatus,
      storeStatusLoading,
      storeStatusError,
      storeFeatureKey,
      storeFeatureValueInput,
      storeFeatureLoading,
      storeFeatureSaving,
      storeFeatureRemoving,
      storeFeatureError,
      storeFeatureResult,

      groupSearch,
      groupResults,
      groupPagination,
      groupSearchLoading,
      groupSearchError,
      selectedGroup,
      groupStatus,
      groupStatusLoading,
      groupStatusError,
      groupFeatureKey,
      groupFeatureLoading,
      groupFeatureError,
      groupFeatureResult,

      featureCatalog,
      visibleFeatureCatalog,
      storeOverrideEntries,
      storeEffectiveFeatureEntries,
      groupFeatureEntries,
      hasSelectedStoreOverride,
      selectedStoreOverrideValue,

      renderFeatureValue,
      formatDate,
      statusBadgeClass,
      accessBadgeClass,
      sourceLabel,
      applyFeatureSelection,
      fetchStoreCandidates,
      fetchGroupCandidates,
      queueStoreSearch,
      queueGroupSearch,
      handleStorePageChange,
      handleGroupPageChange,
      selectStore,
      selectGroup,
      loadStoreStatus,
      loadGroupStatus,
      checkStoreFeature,
      checkGroupFeature,
      saveStoreOverride,
      removeStoreOverride,
    };
  },
};
</script>